/**
 * A certificate requested by a single-use public upload link (piece 10;
 * decision 0679): the link's guards, the file check the upload runs, the
 * page's answer and the mail that carries the link. DOM-free, so the API and
 * the page read one set of numbers.
 */

/** 🔒 The token's length in random bytes; only its SHA-256 is stored. */
export const CERTIFICATE_REQUEST_TOKEN_BYTES = 32;
/** 🔒 A link opens for fourteen days from when it is sent, and once. */
export const CERTIFICATE_REQUEST_DAYS = 14;
/** 🔒 One file of at most ten megabytes. */
export const CERTIFICATE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** 🔒 Ten upload attempts an hour from one client address; the eleventh is refused before anything is read. */
export const CERTIFICATE_UPLOADS_PER_ADDRESS_PER_HOUR = 10;
/** The web page the link opens, outside the shell. */
export const CERTIFICATE_UPLOAD_ROUTE = '/certificate-upload';
/** The kind the public page names beside the company. */
export const CERTIFICATE_REQUEST_KIND = 'Tax exempt certificate';
/** 🔒 One sentence for a link that is unknown, spent, expired or revoked, so the answer tells a stranger nothing. */
export const CERTIFICATE_REQUEST_CLOSED = 'This link can no longer be used. Ask whoever sent it to you for a new one.';

/** Everything the public page is told: the company and the kind, never the site, its address or a person. */
export interface PublicCertificateRequestAnswer {
  companyName: string;
  kind: string;
}

export type CertificateRequestState = 'open' | 'used' | 'expired' | 'revoked';

/** Where a request stands: revoked first, then spent, then past its expiry, else open. */
export function certificateRequestState(row: { usedAt: Date | string | null; revokedAt: Date | string | null; expiresAt: Date | string }, now: Date): CertificateRequestState {
  if (row.revokedAt) return 'revoked';
  if (row.usedAt) return 'used';
  return new Date(row.expiresAt).getTime() <= now.getTime() ? 'expired' : 'open';
}

/** When a link sent now stops opening. */
export function certificateRequestExpiry(sentAt: Date): Date {
  return new Date(sentAt.getTime() + CERTIFICATE_REQUEST_DAYS * 86_400_000);
}

export type CertificateUploadType = 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp' | 'image/heic';

/**
 * 🔒 A PDF or a picture, read off the file's own opening bytes and never off
 * its name or the browser's word (decision 0679); anything else is null.
 */
export function certificateUploadType(bytes: Uint8Array): CertificateUploadType | null {
  const at = (i: number) => bytes[i] ?? -1;
  const ascii = (from: number, to: number) => String.fromCharCode(...Array.from(bytes.subarray(from, to)));
  if (ascii(0, 5) === '%PDF-') return 'application/pdf';
  if (at(0) === 0xff && at(1) === 0xd8 && at(2) === 0xff) return 'image/jpeg';
  if (at(0) === 0x89 && ascii(1, 4) === 'PNG' && at(4) === 0x0d && at(5) === 0x0a) return 'image/png';
  if (ascii(0, 6) === 'GIF87a' || ascii(0, 6) === 'GIF89a') return 'image/gif';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (ascii(4, 8) === 'ftyp' && ['heic', 'heix', 'mif1', 'msf1', 'heim', 'heis'].includes(ascii(8, 12))) return 'image/heic';
  return null;
}

/** Why an upload is refused before it is read, or null: one file, a PDF or a picture, within the cap. */
export function certificateUploadProblem(file: { bytes: Uint8Array } | null): string | null {
  if (!file || file.bytes.length === 0) return 'Choose the certificate file to upload.';
  if (file.bytes.length > CERTIFICATE_UPLOAD_MAX_BYTES) return 'The file is larger than 10 MB. Send a smaller scan or a PDF.';
  if (!certificateUploadType(file.bytes)) return 'Only a PDF or a picture (JPEG, PNG, GIF, WebP or HEIC) can be uploaded.';
  return null;
}

/** The mail the link travels in: who asks, for what, until when, and that it works once. */
export function certificateRequestMail(input: { companyName: string; url: string; expiresAt: Date }): { subject: string; body: string } {
  const until = input.expiresAt.toISOString().slice(0, 10);
  return {
    subject: `Please upload the tax exempt certificate for ${input.companyName}`,
    body: [
      `We need a current tax exempt certificate on file for ${input.companyName}.`,
      `Upload it here, as one PDF or a photo: ${input.url}`,
      `The link works once and until ${until}. No sign-in is needed.`,
    ].join('\n\n'),
  };
}
